/**
 * CANONICAL MODULE — app-platform/shared/authToken.ts
 *
 * This file is copied byte-for-byte into every app (`src/lib/authToken.ts`). It imports nothing from
 * any app. `@azure/msal-browser` is the one permitted external import in the auth set, and it lives
 * here so the other four auth modules stay pure.
 *
 * **Fix bugs HERE, then copy outward. Never edit an app's copy in place.**
 *
 * ── The two rules, and why they are absolute ─────────────────────────────────────────────────────
 * From bindr `build/decisions.md:557-565`, after a real regression (Phase 7, finding #30):
 *
 *     A background pass may fail, but it may never navigate.
 *     Anything that navigates has a click behind it.
 *
 * Before that rule, ambient sync escalated to `acquireTokenRedirect` the moment silent renewal
 * needed the user — so a tab regaining visibility, or a reconnect, could throw the whole page out to
 * Microsoft **taking an unsaved draft with it**. Four of the five apps still did some version of
 * this when W9 was designed: contently and trakr redirect, inkling and quil open a popup that
 * browsers block outside a gesture anyway, silently stopping sync with no user-visible signal.
 *
 * So: `acquireToken` is silent-or-throw. It never calls `acquireTokenPopup`, `acquireTokenRedirect`
 * or `loginRedirect`. Recovery is `reauthenticate()`, and only a click may call it.
 *
 * ── Three things that look optional and are not ──────────────────────────────────────────────────
 *
 * **1. `forceRefresh`.** MSAL hands back the same cached access token for up to an hour after Graph
 * has begun rejecting it. The transport must force a refresh on the first 401 and give up on the
 * second; without this knob, callers retry a dead token until the hour is out.
 *
 * **2. The `timed_out` case.** On MSAL v4+ the auth cache is encrypted with a session-scoped key, so
 * after a browser reopen — or with third-party cookies blocked — the cached refresh token is
 * unusable and `acquireTokenSilent` falls back to a hidden iframe that times out. Treated as an
 * ordinary failure it hard-fails every read and write. It means "the user must interact", exactly
 * like a consent prompt, and must map to `InteractiveSignInRequiredError`.
 *
 * **3. A second 401 is an interaction signal too.** A token MSAL considers valid can still be
 * refused by Graph. The transport, not this module, raises that one — it throws the same error type
 * after a forced refresh has already failed, so callers have one thing to catch.
 */

import {
  BrowserAuthError,
  InteractionRequiredAuthError,
  type AccountInfo,
  type IPublicClientApplication,
} from '@azure/msal-browser'

/**
 * The user must interact before syncing can continue.
 *
 * Thrown by the silent token getter, and by the transport on a second consecutive 401. Callers show
 * a signed-out or "sign in again" affordance; **no caller may navigate on it.** Only a click may
 * reach `reauthenticate()`.
 */
export class InteractiveSignInRequiredError extends Error {
  constructor(message = 'Your Microsoft sign-in has expired. Sign in again to keep syncing.') {
    super(message)
    this.name = 'InteractiveSignInRequiredError'
  }
}

export function isInteractiveSignInRequired(error: unknown): error is InteractiveSignInRequiredError {
  return error instanceof InteractiveSignInRequiredError
}

/** True for every MSAL failure that means "the user must interact" — see notes 1 and 2 above. */
function meansInteractionRequired(error: unknown): boolean {
  if (error instanceof InteractionRequiredAuthError) return true
  return error instanceof BrowserAuthError && error.errorCode === 'timed_out'
}

export interface TokenGetterDeps {
  instance: IPublicClientApplication
  scopes: string[]
  /** The active account, or `null` when signed out. Read at call time, never captured at wiring. */
  getAccount: () => AccountInfo | null
}

export type TokenGetter = (options?: { forceRefresh?: boolean }) => Promise<string>

/**
 * A silent-only access-token getter. Never navigates, never opens a popup, never prompts.
 *
 * Throws `InteractiveSignInRequiredError` when the user must interact, and passes every other error
 * through unlabelled so a network failure is not mistaken for an expired sign-in.
 */
export function createTokenGetter(deps: TokenGetterDeps): TokenGetter {
  return async function acquireToken(options?: { forceRefresh?: boolean }): Promise<string> {
    const account = deps.getAccount()
    if (!account) throw new InteractiveSignInRequiredError('Not signed in on this device.')

    try {
      const result = await deps.instance.acquireTokenSilent({
        scopes: deps.scopes,
        account,
        ...(options?.forceRefresh ? { forceRefresh: true } : {}),
      })
      return result.accessToken
    } catch (error) {
      if (meansInteractionRequired(error)) throw new InteractiveSignInRequiredError()
      throw error
    }
  }
}

export interface InteractiveDeps {
  instance: IPublicClientApplication
  scopes: string[]
  getAccount: () => AccountInfo | null
}

/**
 * Re-establish an expired sign-in by navigating to Microsoft.
 *
 * **Only ever call this from a real click** — a retry button, a "Sync now", a failure gate's "Try
 * again". Everything unsaved must be safe before it runs, because it navigates the page away and
 * does not return.
 *
 * Redirect, never popup: a popup relies on `window.opener`, which COOP severs. That is not a
 * preference — it broke a sibling app, and it is why all five apps redirect today.
 */
export async function reauthenticate(deps: InteractiveDeps): Promise<void> {
  const account = deps.getAccount()
  await deps.instance.acquireTokenRedirect({
    scopes: deps.scopes,
    ...(account ? { account } : {}),
  })
}
